// Browser port of the import engine (stops-import/src/engine.js) with UI
// progress callbacks and the IndexedDB journal. The parsing/validation logic
// itself is the shared code in ./engine/ — byte-identical to the CLI's.

import { importApi, NetworkUncertainError } from "./importApi";
import { journal } from "./journal";

// Shared engine core (plain JS, allowJs — ESM syntax; CJS module.exports breaks
// under CRA's production webpack once babel injects its ESM helper imports)
// @ts-ignore — untyped shared JS
import { buildStopPayload } from "./engine/validate";
// @ts-ignore
import { looksLikeMp3 } from "./engine/parseZips";
// @ts-ignore
import { CDN_URL, LANG_ORDER } from "./engine/constants";

export interface Progress {
  phase: "reconcile" | "stop" | "tail" | "verify" | "done";
  stopSl?: number;
  stopName?: string;
  stopsDone: number;
  stopsTotal: number;
  detail: string;
  uploadPct?: number;
}

export interface RunResult {
  created: number[];
  skipped: number[];
  uploads: number;
  tail: { route?: boolean; detail?: boolean; offline?: boolean };
  verify: VerifyResult | null;
}

export interface VerifyResult {
  ok: boolean;
  checked: number;
  totalFiles: number;
  failed: string[];
  failedNonTranscribe: string[];
}

function fname(tourId: number, sl: number, suffix: string) {
  return `t${tourId}_s${String(sl).padStart(2, "0")}${suffix}`;
}

function stopMatcher(stop: any) {
  return (sp: any) => {
    try {
      const names = typeof sp.name === "string" ? JSON.parse(sp.name) : sp.name;
      const enName = (names || []).find((n: any) => n.language === "en")?.text;
      return enName === stop.names.en && Math.abs(parseFloat(sp.lattitude) - stop.lat) < 0.0005;
    } catch {
      return false;
    }
  };
}

/** Read-oriented preflight — mirrors the CLI's engine.preflight. */
export async function preflight(tourId: number, model: any) {
  const issues: any[] = [];
  const push = (severity: string, code: string, message: string) => issues.push({ severity, code, message });

  const all = await importApi.getAllTours();
  const listed = (all || []).find((t: any) => t.id === tourId);
  if (!listed) {
    push("blocker", "tour-not-found", `Tour ${tourId} not found`);
    return { tour: null, issues };
  }
  const tour = await importApi.getTour(tourId);
  if (!tour?.id) {
    push("blocker", "tour-not-found", `Tour ${tourId} not found`);
    return { tour: null, issues };
  }
  const existing = tour.tourPointsDetail || [];
  const resumable = await journal.hasActive(tourId);
  if (existing.length > 0 && !resumable) {
    push("blocker", "tour-not-empty", `Tour already has ${existing.length} stops — the importer only fills empty tours`);
  } else if (existing.length > 0) {
    push("warning", "tour-partial", `Tour has ${existing.length} stops and an unfinished import journal — this run will RESUME (existing stops are kept, missing ones created)`);
  }
  if (!tour.imageUrl) push("warning", "tour-no-image", "Tour has no imageUrl — the app's detail hero and Home cards will be blank");
  if (!tour.thumbnailUrl) push("warning", "tour-no-thumb", "Tour has no thumbnailUrl — city cards/checkout will be blank");
  if (tour.published !== 0 && tour.published !== false) {
    push("warning", "tour-published-state", `Tour published = ${tour.published} — keep it unpublished (0) until you have reviewed the imported stops`);
  }
  const myBokun = tour.bokunId;
  if (myBokun && myBokun !== 0) {
    const clash = (all || []).find((t: any) => t.id !== tour.id && t.bokunId === myBokun);
    if (clash) push("warning", "bokunid-collision", `Tour's bokunId ${myBokun} is also on tour ${clash.id} — the Bokun cron matches by bokunId and could serve the wrong tour`);
  }
  try {
    const tpl = typeof tour.bookingEmailTemplate === "string" ? JSON.parse(tour.bookingEmailTemplate) : tour.bookingEmailTemplate;
    if (!Array.isArray(tpl) || tpl.length === 0) {
      push("warning", "no-email-template", "Tour has no booking email template yet — Bokun redeem emails for this tour would fail. Add it in Edit Tour before going live.");
    } else {
      const langs = tpl.map((v: any) => v.language);
      for (const l of model.languages) {
        if (!langs.includes(l)) push("warning", "template-missing-lang", `Booking email template has no "${l}" variant`);
      }
      for (const v of tpl) {
        // eslint-disable-next-line no-template-curly-in-string
        if (v.text && !v.text.includes("${redeemCode}")) push("warning", "template-no-redeemcode", `Email template (${v.language}) does not contain the redeem-code placeholder`);
      }
    }
  } catch {
    push("warning", "template-unparseable", "Tour's booking email template is not valid JSON — check it in Edit Tour");
  }
  return { tour, issues };
}

/**
 * Execute the import. Validation must have passed and the operator confirmed.
 * Sequential by design: DB stop order = id ASC.
 */
export async function runImport(
  model: any,
  tourId: number,
  onProgress: (p: Progress) => void,
  shouldAbort: () => boolean
): Promise<RunResult> {
  const result: RunResult = { created: [], skipped: [], uploads: 0, tail: {}, verify: null };
  const total = model.stops.length;
  const report = (p: Partial<Progress>) =>
    onProgress({ phase: "stop", stopsDone: result.created.length + result.skipped.length, stopsTotal: total, detail: "", ...p } as Progress);

  const tour = await importApi.getTour(tourId);
  if (!tour?.id) throw new Error(`Tour ${tourId} not found`);
  const placeId = tour.placeId;
  const serverStops = tour.tourPointsDetail || [];
  const resuming = await journal.hasActive(tourId);
  if (serverStops.length > 0 && !resuming) {
    throw new Error(`Tour already has ${serverStops.length} stops and there is no import journal for it — refusing to continue.`);
  }

  // Server reconciliation (resume): consume matches in SL order vs id-ASC rows.
  // NEVER delete a duplicate (tourPoints/delete erases shared Spaces audio).
  const doneSl = new Set<number>();
  if (resuming) {
    report({ phase: "reconcile", detail: "Checking which stops already exist…" });
    const pool = [...serverStops].sort((a: any, b: any) => a.id - b.id);
    for (const s of model.stops) {
      const idx = pool.findIndex(stopMatcher(s));
      if (idx !== -1) {
        doneSl.add(s.sl);
        pool.splice(idx, 1);
      }
    }
  }

  // Journal replay (resume only) + CDN liveness check on reused filenames.
  let journalUploads: Record<number, { image: string | null; audio: Record<string, string> }> = {};
  if (resuming) {
    journalUploads = (await journal.summarize(tourId)).uploads;
    for (const [sl, u] of Object.entries(journalUploads)) {
      if (u.image && !(await importApi.headOk(`${CDN_URL}/imageUpload/${u.image}`))) u.image = null;
      for (const [lang, f] of Object.entries(u.audio)) {
        if (f && !(await importApi.headOk(`${CDN_URL}/audioUpload/${f}`))) delete u.audio[lang];
      }
      void sl;
    }
  }

  const audioLangs: string[] = model.audioLangs || model.languages;
  const langOrder: string[] = LANG_ORDER.filter((l: string) => audioLangs.includes(l));

  for (const stop of model.stops) {
    if (shouldAbort()) throw new Error("Paused by operator — press Resume to continue (nothing is lost).");
    if (doneSl.has(stop.sl)) {
      result.skipped.push(stop.sl);
      continue;
    }
    const prior = journalUploads[stop.sl] || { image: null, audio: {} };

    // 1. image
    let imageFile = prior.image;
    if (!imageFile) {
      report({ stopSl: stop.sl, stopName: stop.names.en, detail: `Uploading image…` });
      imageFile = await importApi.uploadImage(fname(tourId, stop.sl, `.${stop.image.ext}`), stop.image.data, (pct) =>
        report({ stopSl: stop.sl, stopName: stop.names.en, detail: `Uploading image…`, uploadPct: pct })
      );
      await journal.append(tourId, { type: "upload", sl: stop.sl, kind: "image", file: imageFile });
      result.uploads++;
    }
    // 2. audio per selected language (lazy-extracted one at a time)
    const audioFiles: Record<string, string> = { ...prior.audio };
    for (const lang of langOrder) {
      if (audioFiles[lang]) continue;
      if (shouldAbort()) throw new Error("Paused by operator — press Resume to continue (nothing is lost).");
      const f = stop.audio[lang];
      const data = f.getData();
      if (!looksLikeMp3(data)) throw new Error(`Stop ${stop.sl} ${lang}: "${f.entryName}" is not a valid MP3 — fix the zip, then Resume.`);
      report({ stopSl: stop.sl, stopName: stop.names.en, detail: `Uploading ${lang} audio (${(data.length / 1e6).toFixed(1)} MB)…` });
      const serverName = await importApi.uploadAudio(fname(tourId, stop.sl, `_${lang}.mp3`), data, (pct) =>
        report({ stopSl: stop.sl, stopName: stop.names.en, detail: `Uploading ${lang} audio…`, uploadPct: pct })
      );
      await journal.append(tourId, { type: "upload", sl: stop.sl, kind: "audio", lang, file: serverName });
      audioFiles[lang] = serverName;
      result.uploads++;
    }
    // 3. create the stop with EXACTLY the server-returned filenames.
    report({ stopSl: stop.sl, stopName: stop.names.en, detail: `Creating stop…` });
    const payload = buildStopPayload(stop, model.languages, audioLangs, tourId, placeId, { image: imageFile, audio: audioFiles });
    let created: any;
    try {
      created = await importApi.createStop(payload);
    } catch (e) {
      if (!(e instanceof NetworkUncertainError)) throw e;
      // Uncertain outcome: check the server before any resend (a blind resend
      // could duplicate the stop — the one unrecoverable failure mode).
      const fresh = await importApi.getTour(tourId);
      const landed = (fresh.tourPointsDetail || []).some(stopMatcher(stop));
      created = landed ? null : await importApi.createStop(payload);
    }
    await journal.append(tourId, { type: "stop_created", sl: stop.sl, id: created?.id ?? null });
    result.created.push(stop.sl);
    report({ stopSl: stop.sl, stopName: stop.names.en, detail: `Stop created.` });
  }

  // Tail: ALWAYS route -> detail -> offline, on every run and resume (the detail
  // file snapshots tourDirectionUrl, so route must come first).
  onProgress({ phase: "tail", stopsDone: total, stopsTotal: total, detail: "Generating walking route (Mapbox)…" });
  await importApi.generateRoute(tourId);
  result.tail.route = true;
  onProgress({ phase: "tail", stopsDone: total, stopsTotal: total, detail: "Refreshing tour detail file…" });
  await importApi.getTour(tourId); // designed side effect: rewrites tour-details-{id}.json
  result.tail.detail = true;
  onProgress({ phase: "tail", stopsDone: total, stopsTotal: total, detail: "Bumping offline-update timestamp…" });
  await importApi.bumpOffline(tourId);
  result.tail.offline = true;
  await journal.append(tourId, { type: "tail_done" });
  // Transcripts: each stop create fired the backend's background transcription.
  // Deliberately NO bulk transcribe call (see importApi header).

  onProgress({ phase: "verify", stopsDone: total, stopsTotal: total, detail: "Verifying every file on the CDN…" });
  result.verify = await verifyTour(tourId, audioLangs);
  await journal.append(tourId, { type: "verify", ok: result.verify.ok, failed: result.verify.failed.length });
  await journal.archive(tourId);
  onProgress({ phase: "done", stopsDone: total, stopsTotal: total, detail: "Import finished." });
  return result;
}

/** HEAD-check every file in the tour's live download manifest. */
export async function verifyTour(tourId: number, langs: string[]): Promise<VerifyResult> {
  const manifest = await importApi.downloadManifest(tourId, langs);
  const files: any[] = manifest?.files || [];
  const failed: string[] = [];
  let checked = 0;
  const queue = [...files];
  const workers = Array.from({ length: 4 }, async () => {
    while (queue.length) {
      const f = queue.shift();
      if (!f?.url) continue;
      const ok = await importApi.headOk(f.url);
      checked++;
      if (!ok) failed.push(f.url);
    }
  });
  await Promise.all(workers);
  const failedNonTranscribe = failed.filter((u) => !u.includes("/transcribe/"));
  return { ok: failedNonTranscribe.length === 0, checked, totalFiles: files.length, failed, failedNonTranscribe };
}
